import { useDispatch, useSelector } from 'react-redux';
import { isRegulatedLocale } from '@/shared/utility';
import { openModal } from '@/store/actions/modal';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { setLoginOpenedFrom } from '@/store/actions/uiActions';
import {
  HomeHeroAuthenticationStep,
  LoginFlowVariant,
  useHomeHeroAuthenticationStepActions,
} from '@/store/authentication/AuthenticationStore';
import useIsHomePage from '@/hooks/useIsHomePage';
import { scrollToTop } from '@/utils/utils';
import { selectLocale } from '@/store/selectors/commonSelectors';

interface OpenLogin {
  openedFrom: OpenedFrom;
  variant?: LoginFlowVariant;
  isLoggedOut?: boolean;
}

export function useLogin(): { openLogin: (params: OpenLogin) => void } {
  const locale = useSelector(selectLocale);
  const dispatch = useDispatch();
  const { setHomeHeroAuthenticationStep, setLoginFlowVariant } =
    useHomeHeroAuthenticationStepActions();
  const isHomePage = useIsHomePage();

  const openLogin = ({ openedFrom, variant, isLoggedOut = false }: OpenLogin) => {
    dispatch(setLoginOpenedFrom(openedFrom));
    triggerGTag(GTagEvents.open_login, {
      openedFrom: openedFrom,
    });

    // for Sweden & Finland we want to open the PayNPlay modal
    if (isRegulatedLocale(locale)) {
      dispatch(openModal('paynplay', { loggedOut: isLoggedOut }));
      return;
    }

    // for other builds (Normal & Mexico) we have the homepage log/reg flow
    // we want to open the login modal on all non-home pages
    // we want to scroll to top on the homepage

    // if the variant is not set, we want to default it to LoginViaEmail for all locales except Thailand
    const defineLoginVariant = (
      variant: LoginFlowVariant | undefined,
      locale: string,
    ): LoginFlowVariant => {
      if (variant) {
        return variant;
      }
      if (locale === 'th') {
        return LoginFlowVariant.Mobile;
      }
      return LoginFlowVariant.Email;
    };
    const loginVariant = defineLoginVariant(variant, locale);

    setLoginFlowVariant(loginVariant);
    setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.Login);
    if (isHomePage) {
      scrollToTop();
      return;
    }

    if (loginVariant === LoginFlowVariant.Mobile) {
      dispatch(openModal('loginSms', { loggedOut: isLoggedOut }));
      return;
    }

    dispatch(openModal('login', { loggedOut: isLoggedOut }));
  };

  return { openLogin };
}
